import loadInitialRowsFromServer from './loadInitialRowsFromServer';
import type { PerformanceDatapoint } from 'owa-analytics';
import { returnTopExecutingActionDatapoint } from 'owa-analytics';
import { initTableSelectionOnLoad } from 'owa-mail-actions/lib/initTableSelectionOnLoad';
import type { OnInitialTableLoadComplete } from 'owa-mail-loading-action-types';
import { createMailListLoadDatapoint } from 'owa-mail-logging/lib/utils/mailListLoad/mailListLoadDatapointUtils';
import type { ActionSource } from 'owa-mail-store';
import { updateTablesInCache } from 'owa-mail-triage-local-updates/lib/actions/tableCache/updateTablesInCache';
import type { MailFolderTableQuery, SortBy, TableView } from 'owa-mail-list-store';
import {
    type TableQueryType,
    resetTableViewProperties,
    isMailListConversationView,
    getGQLPagingInfoForQueryRowsForJumpTo,
} from 'owa-mail-list-store';
import type { SessionData } from 'owa-service/lib/types/SessionData';
import { DataSource } from 'owa-analytics-types';
import { isOfflineMailDataEnabled } from 'owa-offline-sync-feature-flags';
import { setCurrentLoadedIndexMutator } from './setCurrentLoadedIndexMutator';
import { getGQLPagingInfoForQueryRowsById } from 'owa-mail-list-store/lib/utils/getGqlTypesFromOwsTypes';
import { INITIAL_LOAD_ROW_COUNT } from 'owa-mail-triage-common';

/**
 * Performs the first load for a mail table
 * @param tableView - the given mail tableView
 * @param onInitialTableLoadComplete - callback for when the initial table load has completed
 * @param actionSource action that initiated the switch folder action
 * @param initialSessionData - session data to be sent with the initial load request
 * @param selectedRowId - optional rowId to select after load
 * @param seekData - optional seek data to jump to a specific location in the table
 * @return a promise that resolves when the load initial table has completed either from server or from cache
 */
export default function loadInitialMailTable(
    tableView: TableView,
    onInitialTableLoadComplete: OnInitialTableLoadComplete,
    actionSource: ActionSource,
    initialSessionData?: SessionData,
    selectedRowId?: string,
    seekData?: {
        matchingString: string;
        sortBy: SortBy;
    }
): Promise<void> {
    // Create a new datapoint for ML load only for the folder node click scenario for now.
    if (actionSource === 'FolderNodeClick') {
        createMailListLoadDatapoint(actionSource, tableView);
    }
    setCurrentLoadedIndexMutator(tableView);

    // Init table selection on any cached items we have
    initTableSelectionOnLoad(tableView, actionSource == 'FolderNodeClick');

    const isTableViewOfFolderType = tableView.tableQuery.type == 0;

    // Update tables in cache
    updateTablesInCache(tableView);

    const scenarioType = (tableView.tableQuery as MailFolderTableQuery).scenarioType;

    if (isTableViewOfFolderType) {
        const datapoint = returnTopExecutingActionDatapoint((dp: PerformanceDatapoint) => {
            return dp.getEventName() == 'SwitchMailFolder';
        });

        if (datapoint) {
            datapoint.addToCustomWaterfall(2, 'LoadInitialMailTable_S');
            datapoint.addCustomProperty(
                'isCached',
                tableView.isInitialLoadComplete // table was loaded once before so its in the cache
            );
            datapoint.addCustomProperty('scenario', scenarioType);
            datapoint.addDataSource(
                tableView.isInitialLoadComplete ? DataSource.IN_MEMORY : DataSource.NETWORK
            );
            datapoint.addToCustomWaterfall(
                1,
                'isOfflineSyncEnabled',
                false /* discardIfDefined*/,
                isOfflineMailDataEnabled() ? 'true' : 'false'
            );
        }
    }

    const shouldShowCachedItems =
        tableView.isInitialLoadComplete && tableView.loadedStartIndex === 0;

    // Reset the table view properties, if we are not loaded from the top of the table
    // or if we are selecting a specific row in the list after a sort
    if (tableView.loadedStartIndex !== 0 || selectedRowId || seekData) {
        resetTableViewProperties(tableView);
    }

    // Create custom paging info if we have a selected row ID to maintain selection
    // or if a seekData object (string and sort column) was provided
    let customPagingInfo;
    const isConversationView = isMailListConversationView(tableView);
    if (selectedRowId) {
        customPagingInfo = getGQLPagingInfoForQueryRowsById(
            INITIAL_LOAD_ROW_COUNT,
            isConversationView,
            selectedRowId
        );
    } else if (seekData) {
        customPagingInfo = getGQLPagingInfoForQueryRowsForJumpTo(
            INITIAL_LOAD_ROW_COUNT,
            isConversationView,
            seekData.matchingString,
            (tableView.tableQuery as MailFolderTableQuery).sortBy
        );
    }

    const loadFromServerPromise = loadInitialRowsFromServer(
        tableView,
        false /* isTablePrefetched */,
        onInitialTableLoadComplete,
        initialSessionData,
        shouldShowCachedItems /* skipGqlTimings */,
        customPagingInfo
    );

    // Resolve the initial load promise immediately if we're showing cached items, otherwise resolve when the server returns results
    return shouldShowCachedItems ? Promise.resolve() : loadFromServerPromise;
}
